import { getParquetResourceId, setParquetMetricSink } from './instrumentation';
import { ParquetRequestError } from './requestError';
import {
  mergeParquetFiles,
  normalizeParquetRows,
  readParquetColumnarFromUrl,
  readParquetFromUrl,
  readParquetMetadataFromUrl,
} from './operations';
import { ParquetWorkerRequest, ParquetWorkerResponse } from './types';

export const parquetWorkerRowChunkSize = 10_000;

export const chunkParquetRows = <Row>(rows: Row[], chunkSize = parquetWorkerRowChunkSize): Row[][] => {
  const chunks: Row[][] = [];
  for (let start = 0; start < rows.length; start += chunkSize) chunks.push(rows.slice(start, start + chunkSize));
  return chunks;
};

const HTTPS_URL_REGEX = /https?:\/\/\S+/g;

const sanitizeWorkerError = (error: unknown): string => {
  const message = error instanceof Error ? error.message : 'Unknown parquet worker error';
  return message.replace(HTTPS_URL_REGEX, '[parquet-resource]');
};

export const executeParquetWorkerRequest = async ({
  request,
  emit,
  isCancelled = () => false,
}: {
  request: Exclude<ParquetWorkerRequest, { kind: 'cancel' } | { kind: 'base-rows-chunk' }>;
  emit: (response: ParquetWorkerResponse, transfer?: Transferable[]) => void;
  isCancelled?: () => boolean;
}) => {
  const responseIdentity = { requestId: request.requestId, generation: request.generation };
  setParquetMetricSink(
    request.instrumentationEnabled ? (metric) => emit({ ...responseIdentity, kind: 'metric', metric }) : undefined
  );
  try {
    if (request.kind === 'metadata') {
      const metadata = await readParquetMetadataFromUrl(request.payload, 'worker');
      emit(
        isCancelled()
          ? { ...responseIdentity, kind: 'cancelled' }
          : { ...responseIdentity, kind: 'metadata-success', metadata }
      );
      return;
    }

    const { baseFile, ...readRequest } = request.payload;
    if (readRequest.outputFormat === 'columnar') {
      const { columnarData, metadata, columnsQueried } = await readParquetColumnarFromUrl(
        readRequest,
        'worker',
        (loadedRows, totalRows) => emit({ ...responseIdentity, kind: 'progress', loadedRows, totalRows })
      );
      if (isCancelled()) {
        emit({ ...responseIdentity, kind: 'cancelled' });
        return;
      }
      const transfer: Transferable[] = [columnarData.positions.buffer as ArrayBuffer];
      Object.values(columnarData.columns).forEach((column) => {
        if (ArrayBuffer.isView(column.values)) transfer.push(column.values.buffer as ArrayBuffer);
        if (column.kind !== 'values' && column.validity) transfer.push(column.validity.buffer as ArrayBuffer);
        if (column.kind === 'dictionary' && column.valueCounts) {
          transfer.push(column.valueCounts.buffer as ArrayBuffer);
        }
      });
      emit({ ...responseIdentity, kind: 'columnar-complete', columnarData, metadata, columnsQueried }, transfer);
      return;
    }
    const decodedFile = await readParquetFromUrl(readRequest, 'worker');
    if (isCancelled()) {
      emit({ ...responseIdentity, kind: 'cancelled' });
      return;
    }
    const resourceId = getParquetResourceId(readRequest.parquetFileUrl);
    const assembledFile = baseFile
      ? mergeParquetFiles({ existingData: baseFile, newData: decodedFile, resourceId, thread: 'worker' })
      : decodedFile;
    const normalizedRows = normalizeParquetRows({
      rows: assembledFile.rows,
      resourceId,
      columnCount: assembledFile.columnsQueried.length,
      thread: 'worker',
    });
    const rowChunks = chunkParquetRows(normalizedRows);
    for (let chunkIndex = 0; chunkIndex < rowChunks.length; chunkIndex += 1) {
      if (isCancelled()) {
        emit({ ...responseIdentity, kind: 'cancelled' });
        return;
      }
      emit({
        ...responseIdentity,
        kind: 'rows-chunk',
        chunkIndex,
        rows: rowChunks[chunkIndex],
      });
    }
    emit({
      ...responseIdentity,
      kind: 'rows-complete',
      chunkCount: rowChunks.length,
      metadata: assembledFile.metadata,
      columnsQueried: assembledFile.columnsQueried,
    });
  } catch (error) {
    emit({
      ...responseIdentity,
      kind: 'error',
      message: sanitizeWorkerError(error),
      ...(error instanceof ParquetRequestError ? { requestError: { status: error.status } } : {}),
    });
  } finally {
    setParquetMetricSink();
  }
};
